import { createRoot } from 'react-dom/client'
import './index.css'
import './pathology/pathology-topbar.css'
import App from './App.jsx'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from "react-router-dom";

const queryClient = new QueryClient();
const AUTH_EXEMPT_401_PATTERNS = [
  /\/api\/login(\?|$)/,
  /\/api\/signin(\?|$)/,
  /\/api\/pharmacy\/auth\/(login|sso|refresh|logout)(\?|$)/,
  /\/api\/tenant\/modules(\?|$)/,
  /\/api\/(health|ping)(\?|$)/,
  /\/api\/company-profile(\?|$)/,
  /\/api\/company(\?|$)/,
];

function safeReloadOnce(reason) {
  try {
    const key = `hmis_recover_once_${reason}`;
    if (sessionStorage.getItem(key) === '1') return false;
    sessionStorage.setItem(key, '1');
    const u = new URL(window.location.href);
    u.searchParams.set('b', String(Date.now()));
    window.location.replace(u.toString());
    return true;
  } catch (e) {
    return false;
  }
}

// Guard: some third-party DOM helpers occasionally call removeChild on nodes
// that are no longer attached which can throw in some browsers and crash the
// app. Patch Node.prototype.removeChild to ignore NotFoundError to improve
// resilience in production builds (safe fallback).
try {
  const _origRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function(node) {
    try {
      return _origRemoveChild.call(this, node);
    } catch (err) {
      try {
        // Ignore DOMNotFound type errors and continue; log at debug level
        console.warn('safeRemoveChild ignored error', err && err.message ? err.message : err);
      } catch (e) {}
      return null;
    }
  };
} catch (e) {
  // If patching fails, ignore — it's non-critical.
}

function showFatalOverlay(message) {
  try {
    if (document.getElementById('hmis-fatal-overlay')) return;
    const el = document.createElement('div');
    el.id = 'hmis-fatal-overlay';
    el.style.position = 'fixed';
    el.style.left = '12px';
    el.style.right = '12px';
    el.style.bottom = '12px';
    el.style.zIndex = '2147483647';
    el.style.padding = '10px 12px';
    el.style.borderRadius = '8px';
    el.style.border = '1px solid #f5c2c7';
    el.style.background = '#fff3f3';
    el.style.color = '#7a1f24';
    el.style.font = '12px/1.35 var(--font-family)';
    el.textContent = `Runtime error recovered: ${String(message || 'unknown')}`;
    document.body.appendChild(el);
  } catch (e) {}
}

// Global runtime error capture to help diagnose SyntaxError / VM errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
	console.error("Global error:", {
	        message: e?.message,
		file: e?.filename,
		line: e?.lineno,
		column: e?.colno,
      });

  const msg = String(e?.message || '');
  const filename = String(e?.filename || '');
  const combined = `${msg} ${filename}`.toLowerCase();
  if (combined.includes('removechild') || combined.includes('failed to execute') && combined.includes('node')) {
    const reloaded = safeReloadOnce('removechild');
    if (!reloaded) showFatalOverlay(msg || 'DOM mutation error');
  }
  if (combined.includes('failed to fetch dynamically imported module') || combined.includes('loading chunk') || combined.includes('chunkloaderror')) {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('b', String(Date.now()));
      window.location.replace(u.toString());
      return;
    } catch (e2) {
      try { window.location.reload(); } catch (e3) {}
    }
  }

	if (e?.error?.stack) console.error(e.error.stack);
     });

  window.addEventListener('unhandledrejection', (e) => {
     console.error('Unhandled promise:', e?.reason);
     const msg = String(e?.reason?.message || e?.reason || '');
     const lower = msg.toLowerCase();
    if (lower.includes('failed to fetch dynamically imported module') || lower.includes('chunkloaderror') || lower.includes('loading chunk')) {
       try {
         const u = new URL(window.location.href);
         u.searchParams.set('b', String(Date.now()));
         window.location.replace(u.toString());
         return;
       } catch (e2) {
         try { window.location.reload(); } catch (e3) {}
       }
     }
      if (e?.reason?.stack) console.error(e.reason.stack);
  });

  window.__hmisHandleExpiredSession = (reason = 'expired-token') => {
    try {
      if (window.__hmisSessionRedirecting) return;
      window.__hmisSessionRedirecting = true;
      try {
        localStorage.removeItem('hmis_user');
        localStorage.removeItem('hmis_token');
        localStorage.removeItem('hmis_last_page');
        localStorage.removeItem('hmis_session_last_activity');
        localStorage.removeItem('pharmacyToken');
        localStorage.removeItem('pharmacyUser');
      } catch (e) {}
      try { window.dispatchEvent(new CustomEvent('hmis:sessionExpired', { detail: { reason } })); } catch (e) {}
      try {
        if (window && typeof window.appNavigate === 'function') {
          window.appNavigate('login');
          return;
        }
      } catch (e) {}
      try {
        window.dispatchEvent(new CustomEvent('hmis:navigate', { detail: { page: 'login' } }));
        return;
      } catch (e) {}
      try { window.location.hash = '#/login'; } catch (e) {}
    } catch (e) {
      try { window.location.hash = '#/login'; } catch (err) {}
    }
  };
}

// Intercept global fetch calls to detect expired sessions and redirect to login
if (typeof window !== 'undefined' && window.fetch) {
     const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      let skip401Redirect = false;
      let url = '';
      try {
        const hmisToken = localStorage.getItem('hmis_token');
        const pharmacyToken = localStorage.getItem('pharmacyToken');
        const rawUrl = args[0] instanceof Request ? args[0].url : String(args[0] || '');
        url = rawUrl || '';
        const isApiRequest = /\/api(\/|$)/.test(url) || url.startsWith('/api');
        const isLoginRequest = /\/api\/login(\?|$)/.test(url) || url === '/api/login';
        const isPharmacyApiRequest = /\/api\/pharmacy(\/|$)/.test(url) || url.startsWith('/api/pharmacy');
        skip401Redirect = !!(args[1] && args[1].hmisSkip401Redirect);

        const preferredToken = isPharmacyApiRequest ? (pharmacyToken || hmisToken) : hmisToken;

        if (preferredToken && isApiRequest && !isLoginRequest) {
          if (args[0] instanceof Request) {
            const req = args[0];
            const existingAuth = req.headers.get('Authorization') || req.headers.get('authorization');
            if (!existingAuth) {
              args[0] = new Request(req, {
			headers: {
			...Object.fromEntries(req.headers.entries()),
			Authorization: "Bearer " + preferredToken,
			}
		});
            }
          } else {
            args[1] = args[1] || {};
            const existingHeaders = args[1].headers instanceof Headers
              ? Object.fromEntries(args[1].headers.entries())
              : (args[1].headers || {});
            const hasAuthorization = Object.keys(existingHeaders || {}).some((key) => String(key).toLowerCase() === 'authorization');
            if (!hasAuthorization) {
              args[1].headers = {
                ...existingHeaders,
                Authorization: 'Bearer ' + preferredToken,
              };
            }
          }
        }
      } catch (err) {
	console.warn("Token attach failed", err); 
 }

      const response = await originalFetch(...args);

        if (response.status === 401 && !skip401Redirect) {
          try {
            const respUrl = (response && response.url) || (args && args[0] && (args[0].url || args[0])) || url || '';
            const isExempt = AUTH_EXEMPT_401_PATTERNS.some((pattern) => pattern.test(respUrl));
            try { console.warn('globalFetch: 401 response', { url: respUrl, hasToken: !!localStorage.getItem('hmis_token'), isExempt }); } catch (ee) {}
            if (!isExempt && (localStorage.getItem('hmis_token') || localStorage.getItem('pharmacyToken'))) {
              try { window.__hmisHandleExpiredSession('http-401'); } catch (e) {}
            }
          } catch (e) {}
        }
      return response;
    };
  }

createRoot(document.getElementById('root')).render(
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </QueryClientProvider>
);

// On startup, ensure we clear tenant-scoped localStorage if current token belongs to SuperAdmin.
try {
  const token = localStorage.getItem('hmis_token');
  if (token && token.split('.').length === 3) {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload && (payload.isSuperAdmin === true || payload.is_superadmin === true || payload.superadmin === true)) {
      try {
        const u = JSON.parse(localStorage.getItem('hmis_user') || '{}') || {};
        u.tenant_id = null; u.tenantId = null; u.company_code = null;
        u.is_superadmin = true; u.isSuperAdmin = true;
        localStorage.setItem('hmis_user', JSON.stringify(u));
      } catch (e) {}
      try { localStorage.removeItem('visibleModules'); } catch (e) {}
      try { localStorage.removeItem('hmis_single_module'); } catch (e) {}
    }
  }
} catch (e) {}
